import { AssignedBubble } from '@/components/global/AssignedBubble';
import { GenericToolbar, type ToolbarFilterOption } from '@/components/ui/generic-toolbar/GenericToolbar';
import { PaginationControls } from '@/components/ui/pagination-controls';
import {
    ResponsiveModal,
    ResponsiveModalBody,
    ResponsiveModalContent,
    ResponsiveModalDescription,
    ResponsiveModalFooter,
    ResponsiveModalHeader,
    ResponsiveModalTitle,
} from '@/components/ui/responsive-modal';
import { Badge } from '@/components/ui/shadcn/badge';
import { Button } from '@/components/ui/shadcn/button';
import { DatePicker } from '@/components/ui/shadcn/date-picker';
import { Skeleton } from '@/components/ui/shadcn/skeleton';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/shadcn/table';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslations } from '@/hooks/useTranslations';
import { buildAssignedUserOptions } from '@/lib/assigned-user-options';
import { cn, formatDateTime } from '@/lib/utils';
import { ArrowRight, FolderKanban, History, Pencil, PlusCircle, RotateCcw, SignpostBig, Trash2, UserCog } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { route } from 'ziggy-js';

/** The buckets ProjectHistoryQuery sorts every entry into. */
type HistoryType = 'create' | 'update' | 'delete' | 'revert' | 'assignment' | 'status' | 'link' | 'project';

interface HistoryPerson {
    id: string;
    first_name?: string | null;
    last_name?: string | null;
    avatar?: string | null;
}

interface HistoryRow {
    id: string;
    source: 'task' | 'project';
    task_uuid: string | null;
    task_name: string | null;
    is_project_timeline: boolean;
    assignee: HistoryPerson | null;
    type: string;
    description: string;
    properties: Record<string, unknown> | null;
    changed_at: string | null;
    changed_by_user: HistoryPerson | null;
    /** Set only for task edits — a project event has no snapshot to restore. */
    revert_task_uuid: string | null;
}

interface HistoryFilterOptions {
    users: HistoryPerson[];
    assignees: HistoryPerson[];
    types: HistoryType[];
    tasks: Array<{ id: string; name: string | null; is_project_timeline: boolean }>;
    has_project_entries: boolean;
}

interface PaginatedHistory {
    data: HistoryRow[];
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
    from: number | null;
    to: number | null;
}

interface SnapshotOption {
    id: string;
    change_type: 'create' | 'update' | 'delete' | 'revert';
    description: string;
    changed_at: string;
    snapshot: Record<string, unknown>;
}

interface Props {
    projectId: string;
    canRevert: boolean;
}

/** The sentinel the server understands as "entries about the project itself". */
const PROJECT_SCOPE = 'project';

const COLUMN_IDS = ['changed_at', 'record', 'type', 'description', 'user'] as const;

function typeIcon(type: string): ReactNode {
    switch (type) {
        case 'create':
            return <PlusCircle className="text-success-subtle-foreground h-4 w-4 shrink-0" />;
        case 'update':
            return <Pencil className="text-warning-subtle-foreground h-4 w-4 shrink-0" />;
        case 'delete':
            return <Trash2 className="text-destructive h-4 w-4 shrink-0" />;
        case 'revert':
            return <RotateCcw className="text-info-subtle-foreground h-4 w-4 shrink-0" />;
        case 'assignment':
            return <UserCog className="text-info-subtle-foreground h-4 w-4 shrink-0" />;
        case 'status':
            return <SignpostBig className="text-warning-subtle-foreground h-4 w-4 shrink-0" />;
        case 'link':
            return <FolderKanban className="text-info-subtle-foreground h-4 w-4 shrink-0" />;
        default:
            return <History className="text-muted-foreground h-4 w-4 shrink-0" />;
    }
}

/**
 * Task-log descriptions arrive as "Title: a → b; c → d". Split so the title can
 * lead and each change reads as its own chip, rather than one long line.
 */
function splitDescription(description: string): { title: string; details: string[] } {
    const trimmed = description.trim();
    if (!trimmed) {
        return { title: '', details: [] };
    }

    const separator = trimmed.indexOf(':');
    if (separator === -1) {
        return { title: trimmed, details: [] };
    }

    const title = trimmed.slice(0, separator).trim();
    const details = trimmed
        .slice(separator + 1)
        .split(';')
        .map((part) => part.trim().replace(/\s+į\s+/gi, ' → '))
        .filter(Boolean);

    return { title: title || trimmed, details };
}

function asText(value: unknown): string | null {
    if (value === null || value === undefined || value === '') {
        return null;
    }

    return typeof value === 'object' ? null : String(value);
}

/**
 * Everything that has happened to a project, in one table.
 *
 * Task edits and project-level changes (staffing, status, links) used to be two
 * separate tabs drawing on two tables, so "what happened here" was a question
 * you had to ask twice. The server merges them; this renders the one stream with
 * the same toolbar every index page uses.
 */
export default function ProjectHistoryTab({ projectId, canRevert }: Props) {
    const { t } = useTranslations();

    const [history, setHistory] = useState<PaginatedHistory | null>(null);
    const [options, setOptions] = useState<HistoryFilterOptions | null>(null);
    const [loading, setLoading] = useState(true);

    const [page, setPage] = useState(1);
    const [perPage, setPerPage] = useState(25);
    const [search, setSearch] = useState('');
    const debouncedSearch = useDebounce(search, 350);
    const [columnFilters, setColumnFilters] = useState<Record<string, string[]>>({});
    const [columnFilterSearch, setColumnFilterSearch] = useState<Record<string, string>>({});
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [isSortOpen, setIsSortOpen] = useState(false);
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(new Set(COLUMN_IDS));

    const [revertModalOpen, setRevertModalOpen] = useState(false);
    const [selectedTaskUuid, setSelectedTaskUuid] = useState<string | null>(null);
    const [snapshotOptions, setSnapshotOptions] = useState<SnapshotOption[]>([]);
    const [selectedLogId, setSelectedLogId] = useState('');
    const [loadingSnapshots, setLoadingSnapshots] = useState(false);

    // A stable signature keeps the effect from re-firing on every render just
    // because the filter object is a fresh literal.
    const filterSignature = useMemo(
        () => JSON.stringify({ columnFilters, debouncedSearch, dateFrom, dateTo, page, perPage }),
        [columnFilters, debouncedSearch, dateFrom, dateTo, page, perPage],
    );

    const fetchHistory = useCallback(async () => {
        setLoading(true);

        const params = new URLSearchParams();
        params.set('page', String(page));
        params.set('per_page', String(perPage));
        if (debouncedSearch.trim()) params.set('search', debouncedSearch.trim());
        if (dateFrom) params.set('date_from', dateFrom);
        if (dateTo) params.set('date_to', dateTo);
        (columnFilters.type ?? []).forEach((value) => params.append('type[]', value));
        (columnFilters.user ?? []).forEach((value) => params.append('user_id[]', value));
        (columnFilters.task ?? []).forEach((value) => params.append('task_uuid[]', value));
        (columnFilters.assignee ?? []).forEach((value) => params.append('assignee_id[]', value));

        try {
            const response = await fetch(`${route('projects.tasks.logs', projectId)}?${params.toString()}`, {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });
            const data = await response.json();
            setHistory(data.logs as PaginatedHistory);
            setOptions(data.filterOptions as HistoryFilterOptions);
        } finally {
            setLoading(false);
        }
        // filterSignature stands in for every input above; listing them again would
        // re-run this on each render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [projectId, filterSignature]);

    useEffect(() => {
        void fetchHistory();
    }, [fetchHistory]);

    const columnDefinitions = useMemo(
        () => [
            { id: 'changed_at', label: t('projects.logs.columns.date', 'Date') },
            { id: 'record', label: t('projects.history.columns.record', 'Record') },
            { id: 'type', label: t('projects.logs.columns.type', 'Type') },
            { id: 'description', label: t('projects.logs.columns.description', 'Description') },
            { id: 'user', label: t('projects.logs.columns.user', 'User') },
        ],
        [t],
    );

    const filterDefinitions = useMemo(
        () => [
            { id: 'type', label: t('projects.logs.filters.type', 'Type') },
            { id: 'user', label: t('projects.logs.filters.user', 'User') },
            { id: 'task', label: t('projects.history.filters.task', 'Task') },
            { id: 'assignee', label: t('projects.history.filters.assignee', 'Assignment') },
        ],
        [t],
    );

    const columnFilterOptions = useMemo<Record<string, ToolbarFilterOption[]>>(() => {
        if (!options) {
            return {};
        }

        const built: Record<string, ToolbarFilterOption[]> = {
            type: options.types.map((type) => ({ value: type, label: t(`projects.history.types.${type}`, type) })),
            // Grouped like every other people filter in the app.
            user: buildAssignedUserOptions(options.users, t),
            assignee: buildAssignedUserOptions(options.assignees, t),
            task: [
                // Project-level entries belong to no task, so they get an explicit
                // entry rather than quietly disappearing when this filter is used.
                ...(options.has_project_entries
                    ? [{ value: PROJECT_SCOPE, label: t('projects.history.filters.project_itself', 'The project itself') }]
                    : []),
                ...options.tasks.map((task) => ({
                    value: task.id,
                    label: task.name || t('projects.logs.unknown_task', 'Deleted task'),
                    group: task.is_project_timeline
                        ? t('projects.history.filters.timeline_group', 'Project timeline')
                        : t('projects.history.filters.tasks_group', 'Tasks'),
                })),
            ],
        };

        return built;
    }, [options, t]);

    const activeFilterLabels = filterDefinitions.filter((filter) => (columnFilters[filter.id] ?? []).length > 0).map((filter) => filter.label);
    const quickFilterButtonLabel =
        activeFilterLabels.length > 0
            ? `${t('projects.history.toolbar.quick_filters', 'Filters')}: ${activeFilterLabels.join(', ')}`
            : t('projects.history.toolbar.all', 'All changes');

    const handleFilterChange = (column: string, values: string[]) => {
        setPage(1);
        setColumnFilters((previous) => {
            const next = { ...previous };
            if (values.length === 0) {
                delete next[column];
            } else {
                next[column] = values;
            }
            return next;
        });
    };

    const openRevertModal = async (taskUuid: string) => {
        setSelectedTaskUuid(taskUuid);
        setRevertModalOpen(true);
        setLoadingSnapshots(true);

        try {
            const response = await fetch(route('projects.tasks.snapshots', { project: projectId, taskUuid }), {
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });
            const data = await response.json();
            const snapshots = (data.snapshots ?? []) as SnapshotOption[];
            setSnapshotOptions(snapshots);
            setSelectedLogId(snapshots[0]?.id ?? '');
        } finally {
            setLoadingSnapshots(false);
        }
    };

    const handleRevert = async () => {
        if (!selectedTaskUuid || !selectedLogId) {
            return;
        }

        await fetch(route('projects.tasks.revert', { project: projectId, taskUuid: selectedTaskUuid }), {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement)?.content ?? '',
            },
            body: JSON.stringify({ log_id: selectedLogId }),
            credentials: 'same-origin',
        });

        setRevertModalOpen(false);
        await fetchHistory();
    };

    const paginationMeta = {
        current_page: history?.current_page ?? 1,
        last_page: history?.last_page ?? 1,
        per_page: history?.per_page ?? perPage,
        total: history?.total ?? 0,
        from: history?.from ?? null,
        to: history?.to ?? null,
    };

    const columns = columnDefinitions.filter((column) => visibleColumns.has(column.id));
    const showRevertColumn = canRevert;

    return (
        <div className="space-y-4">
            <GenericToolbar
                quickFilterButtonLabel={quickFilterButtonLabel}
                quickFilterDefinitions={filterDefinitions}
                columnDefinitions={columnDefinitions}
                columnFilterOptions={columnFilterOptions}
                columnFilterLoading={{ type: !options, user: !options, task: !options, assignee: !options }}
                columnFilters={columnFilters}
                columnFilterSearch={columnFilterSearch}
                setColumnFilterSearch={setColumnFilterSearch}
                toggleSingleFilter={(column, value) => {
                    const current = columnFilters[column] ?? [];
                    handleFilterChange(column, current.includes(value) ? current.filter((entry) => entry !== value) : [...current, value]);
                }}
                handleFilterChange={handleFilterChange}
                clearFilters={() => {
                    setColumnFilters({});
                    setSearch('');
                    setDateFrom('');
                    setDateTo('');
                    setPage(1);
                }}
                isFilterOpen={isFilterOpen}
                setIsFilterOpen={setIsFilterOpen}
                isSortOpen={isSortOpen}
                setIsSortOpen={setIsSortOpen}
                // The timeline is always newest-first; a sort menu here would offer
                // an ordering the endpoint does not implement.
                sortColumn={null}
                sortDirection={null}
                handleSortChange={() => undefined}
                clearSort={() => undefined}
                search={search}
                setSearch={(value) => {
                    setPage(1);
                    setSearch(value);
                }}
                perPage={perPage}
                onPerPageChange={(value) => {
                    setPerPage(value);
                    setPage(1);
                }}
                viewMode="table"
                setViewMode={() => undefined}
                showViewToggle={false}
                visibleColumns={visibleColumns}
                handleColumnShow={(columnId) => setVisibleColumns((previous) => new Set(previous).add(columnId))}
                handleColumnHide={(columnId) =>
                    setVisibleColumns((previous) => {
                        const next = new Set(previous);
                        next.delete(columnId);
                        return next;
                    })
                }
                filteredCount={paginationMeta.total}
                // The toolbar prints the plural verbatim next to the count, and
                // resolves `<resourceName>.index.toolbar.found` for the suffix.
                resourceName="projects"
                resourceNamePlural={t('projects.history.entries', 'changes')}
                // Quick filters are the whole set here, so the column list would only
                // repeat them.
                showMoreFilters={false}
                customFiltersPosition="end"
                customFilters={
                    <div className="space-y-3">
                        <div className="flex items-center justify-between text-sm font-medium">
                            <span>{t('projects.history.filters.date_range', 'Date range')}</span>
                            <Button
                                type="button"
                                variant="ghost"
                                size="sm"
                                className="text-muted-foreground h-auto px-2 py-1 text-xs"
                                disabled={!dateFrom && !dateTo}
                                onClick={() => {
                                    setDateFrom('');
                                    setDateTo('');
                                    setPage(1);
                                }}
                            >
                                {t('common.reset', 'Reset')}
                            </Button>
                        </div>
                        <div className="grid gap-1">
                            <label className="text-muted-foreground text-xs">{t('projects.history.filters.from_date', 'From date')}</label>
                            <DatePicker
                                value={dateFrom}
                                onValueChange={(value) => {
                                    setPage(1);
                                    setDateFrom(value);
                                }}
                                placeholder={t('common.pick_date', 'Pick a date')}
                            />
                        </div>
                        <div className="grid gap-1">
                            <label className="text-muted-foreground text-xs">{t('projects.history.filters.to_date', 'To date')}</label>
                            <DatePicker
                                value={dateTo}
                                onValueChange={(value) => {
                                    setPage(1);
                                    setDateTo(value);
                                }}
                                placeholder={t('common.pick_date', 'Pick a date')}
                            />
                        </div>
                    </div>
                }
            />

            <div className="bg-card mb-4 rounded-xl border px-4 py-2.5">
                <Table>
                    <TableHeader>
                        <TableRow>
                            {columns.map((column) => (
                                <TableHead key={column.id}>{column.label}</TableHead>
                            ))}
                            {showRevertColumn ? <TableHead className="w-px">{t('projects.logs.columns.actions', 'Actions')}</TableHead> : null}
                        </TableRow>
                    </TableHeader>
                    <TableBody>
                        {loading && !history ? (
                            [0, 1, 2, 3].map((row) => (
                                <TableRow key={row}>
                                    <TableCell colSpan={columns.length + (showRevertColumn ? 1 : 0)}>
                                        <Skeleton className="h-8 w-full" />
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : history?.data.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={columns.length + (showRevertColumn ? 1 : 0)} className="text-muted-foreground py-8 text-center">
                                    {t('projects.history.empty', 'Nothing has changed on this project yet.')}
                                </TableCell>
                            </TableRow>
                        ) : (
                            history?.data.map((row) => {
                                const description = splitDescription(row.description);
                                const from = asText(row.properties?.old);
                                const to = asText(row.properties?.new);
                                const note = asText(row.properties?.note);
                                const isProjectRow = row.source === 'project';

                                return (
                                    <TableRow key={row.id}>
                                        {columns.map((column) => {
                                            if (column.id === 'changed_at') {
                                                return (
                                                    <TableCell key={column.id} className="whitespace-nowrap">
                                                        {row.changed_at ? formatDateTime(row.changed_at) : '—'}
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'record') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        {isProjectRow ? (
                                                            <Badge variant="secondary">
                                                                {t('projects.history.filters.project_itself', 'The project itself')}
                                                            </Badge>
                                                        ) : (
                                                            <span className={cn('text-sm', row.is_project_timeline && 'font-medium')}>
                                                                {row.task_name || t('projects.logs.unknown_task', 'Deleted task')}
                                                            </span>
                                                        )}
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'type') {
                                                const bucket = isProjectRow ? historyBucket(row.type) : row.type;

                                                return (
                                                    <TableCell key={column.id} className="whitespace-nowrap">
                                                        <span className="flex items-center gap-2 text-sm">
                                                            {typeIcon(bucket)}
                                                            {t(`projects.history.types.${bucket}`, bucket)}
                                                        </span>
                                                    </TableCell>
                                                );
                                            }

                                            if (column.id === 'description') {
                                                return (
                                                    <TableCell key={column.id}>
                                                        <div className="max-w-[560px] space-y-1">
                                                            <p className="truncate text-sm font-medium">
                                                                {isProjectRow
                                                                    ? t(`projects.show.history.events.${row.type}`, row.description || row.type)
                                                                    : description.title}
                                                            </p>
                                                            {/* Project entries carry one before/after pair; task
                                                                entries carry several, already formatted. */}
                                                            {isProjectRow && (from !== null || to !== null) ? (
                                                                <p className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs">
                                                                    <span className="line-through">
                                                                        {from ?? t('projects.show.history.none', 'none')}
                                                                    </span>
                                                                    <ArrowRight className="h-3 w-3 shrink-0" />
                                                                    <span className="text-foreground font-medium">
                                                                        {to ?? t('projects.show.history.none', 'none')}
                                                                    </span>
                                                                </p>
                                                            ) : null}
                                                            {note !== null ? <p className="text-xs italic">{note}</p> : null}
                                                            {description.details.length > 0 ? (
                                                                <div className="text-muted-foreground flex flex-wrap items-center gap-1.5 text-xs">
                                                                    {description.details.map((detail, index) => (
                                                                        <span
                                                                            key={`${row.id}-detail-${index}`}
                                                                            className="border-muted bg-muted/20 inline-flex max-w-[380px] items-center gap-1 rounded-md border px-2 py-0.5"
                                                                        >
                                                                            <ArrowRight className="h-3 w-3 shrink-0" />
                                                                            <span className="truncate">{detail}</span>
                                                                        </span>
                                                                    ))}
                                                                </div>
                                                            ) : null}
                                                        </div>
                                                    </TableCell>
                                                );
                                            }

                                            return (
                                                <TableCell key={column.id} className="w-px">
                                                    {/* Same bubble the tables use, so a person reads
                                                        identically wherever the app shows one. */}
                                                    <AssignedBubble person={row.changed_by_user} role={t('projects.logs.columns.user', 'User')} />
                                                </TableCell>
                                            );
                                        })}
                                        {showRevertColumn ? (
                                            <TableCell className="w-px">
                                                {row.revert_task_uuid ? (
                                                    <Button
                                                        type="button"
                                                        size="sm"
                                                        variant="outline"
                                                        onClick={() => openRevertModal(row.revert_task_uuid as string)}
                                                    >
                                                        {t('projects.logs.actions.revert', 'Revert')}
                                                    </Button>
                                                ) : null}
                                            </TableCell>
                                        ) : null}
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </div>

            <PaginationControls
                pagination={paginationMeta}
                onPageChange={setPage}
                onPerPageChange={(value) => {
                    setPerPage(value);
                    setPage(1);
                }}
                loading={loading}
            />

            <ResponsiveModal open={revertModalOpen} onOpenChange={setRevertModalOpen} variant="sheet">
                <ResponsiveModalContent size="lg">
                    <ResponsiveModalHeader>
                        <ResponsiveModalTitle>{t('projects.logs.revert.title', 'Revert Task')}</ResponsiveModalTitle>
                        <ResponsiveModalDescription>
                            {t('projects.logs.revert.description', 'Select the historic version to restore for this task.')}
                        </ResponsiveModalDescription>
                    </ResponsiveModalHeader>

                    <ResponsiveModalBody className="space-y-2">
                        {loadingSnapshots ? <Skeleton className="h-16 w-full" /> : null}
                        {!loadingSnapshots && snapshotOptions.length === 0 ? (
                            <p className="text-muted-foreground text-sm">
                                {t('projects.logs.revert.no_snapshots', 'No snapshots available for this task.')}
                            </p>
                        ) : null}
                        {snapshotOptions.map((option) => (
                            <label
                                key={option.id}
                                className={cn(
                                    'flex cursor-pointer items-start gap-2 rounded-md border p-2 text-sm',
                                    selectedLogId === option.id && 'border-primary',
                                )}
                            >
                                <input
                                    type="radio"
                                    name="snapshot"
                                    value={option.id}
                                    checked={selectedLogId === option.id}
                                    onChange={() => setSelectedLogId(option.id)}
                                />
                                <span>
                                    <strong className="inline-flex items-center gap-1">
                                        {typeIcon(option.change_type)}
                                        <span>{t(`projects.history.types.${option.change_type}`, option.change_type)}</span>
                                    </strong>{' '}
                                    — {option.changed_at ? formatDateTime(option.changed_at) : '—'}
                                    <br />
                                    <span className="text-muted-foreground">{option.description}</span>
                                </span>
                            </label>
                        ))}
                    </ResponsiveModalBody>

                    <ResponsiveModalFooter>
                        <Button type="button" variant="outline" onClick={() => setRevertModalOpen(false)}>
                            {t('projects.logs.revert.cancel', 'Cancel')}
                        </Button>
                        <Button type="button" onClick={handleRevert} disabled={!selectedLogId}>
                            {t('projects.logs.revert.confirm', 'Revert selected version')}
                        </Button>
                    </ResponsiveModalFooter>
                </ResponsiveModalContent>
            </ResponsiveModal>
        </div>
    );
}

/**
 * Mirrors ProjectHistoryQuery::category — a project's many named events collapse
 * into the same three buckets the Type filter offers, so the column and the
 * filter agree on what a row is.
 */
function historyBucket(event: string): HistoryType {
    if (['owner_changed', 'project_manager_changed', 'project_engineer_changed', 'contact_changed'].includes(event)) {
        return 'assignment';
    }
    if (['calculation_linked', 'calculation_unlinked'].includes(event)) {
        return 'link';
    }
    if (event === 'status_changed') {
        return 'status';
    }

    return 'project';
}
